'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { InternalNetworkAttackPathEntityTable } from './InternalNetworkAttackPathEntityTable';
import type { InternalNetworkAttackPathEntity } from './internal-scope.model';

export type InternalNetworkAttackPathScopeListsProps = {
  ownedPrincipals: InternalNetworkAttackPathEntity[];
  crownJewels: InternalNetworkAttackPathEntity[];
  onRevokeOwnedPrincipal: (entityId: string) => void;
  onDeleteCrownJewel: (crownJewelId: string) => void;
  isOwnedPrincipalsLoading?: boolean;
  isCrownJewelsLoading?: boolean;
  isRevokingOwnedPrincipal?: boolean;
  isDeletingCrownJewel?: boolean;
  disabled?: boolean;
  actions?: ReactNode;
  tableMaxHeight?: string;
};

export function InternalNetworkAttackPathScopeLists({
  ownedPrincipals,
  crownJewels,
  onRevokeOwnedPrincipal,
  onDeleteCrownJewel,
  isOwnedPrincipalsLoading,
  isCrownJewelsLoading,
  isRevokingOwnedPrincipal,
  isDeletingCrownJewel,
  disabled,
  actions,
  tableMaxHeight,
}: InternalNetworkAttackPathScopeListsProps) {
  const t = useTranslations(
    'designSystem.networkSettings.internalScope.attackPath',
  );
  const tScope = useTranslations('designSystem.networkSettings.internalScope');

  return (
    <VStack alignItems="stretch" gap={6} w="full">
      {actions ? (
        <HStack gap={2} justifyContent="flex-end" w="full">
          {actions}
        </HStack>
      ) : null}
      <InternalNetworkAttackPathEntityTable
        title={t('ownedPrincipals.title')}
        description={t('ownedPrincipals.description')}
        entities={ownedPrincipals}
        nameColumnTitle={t('principalColumn')}
        searchPlaceholder={tScope('searchPlaceholder')}
        emptyStateTitle={t('ownedPrincipals.emptyTitle')}
        emptyStateDescription={t('ownedPrincipals.emptySetupHint')}
        removeLabel={t('ownedPrincipals.unmark')}
        onRemove={(entity) => onRevokeOwnedPrincipal(entity.entityId)}
        isLoading={isOwnedPrincipalsLoading}
        disabled={disabled || isRevokingOwnedPrincipal}
        tableMaxHeight={tableMaxHeight}
      />
      <InternalNetworkAttackPathEntityTable
        title={t('crownJewels.title')}
        description={t('crownJewels.description')}
        entities={crownJewels}
        nameColumnTitle={t('crownJewels.nameColumn')}
        searchPlaceholder={t('crownJewels.searchPlaceholder')}
        emptyStateTitle={t('crownJewels.emptyTitle')}
        emptyStateDescription={t('crownJewels.emptySetupHint')}
        removeLabel={t('crownJewels.unmark')}
        onRemove={(entity) => onDeleteCrownJewel(entity.id)}
        isLoading={isCrownJewelsLoading}
        disabled={disabled || isDeletingCrownJewel}
        tableMaxHeight={tableMaxHeight}
      />
    </VStack>
  );
}
